import { useEffect, useState, useMemo, useRef, memo, useCallback } from 'react';
import { join } from 'light-component';
import { Select as ASelect, Tag, Space } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { useBreakpoint } from '../../../../hooks';
import { t } from '../../../i18n';
import styles from '../style.module.less';
import { useDeepCompareMemoize } from './useDeepCompareMemoize';
import type { ReactElement } from 'react';
import type { SelectProps as ASelectProps } from 'antd';
import type { BaseSelectRef } from 'rc-select';
import type { SelectOption, SelectProps } from '../types';

const SELECT_ALL = 'all';

export const Select = memo((props: SelectProps): ReactElement => {
  const {
    disabled = false,
    value,
    loading = false,
    options,
    placeholder,
    onChange,
    labelRender,
    maxTagCount = 1,
    allowClear = false
  } = props;

  const selectRef = useRef<BaseSelectRef | null>(null);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const { md } = useBreakpoint();
  const [selected, setSelected] = useState<string[]>([]);
  const [keyWords, setKeyWords] = useState('');

  const optionsMemo = useDeepCompareMemoize(options);
  const selectOptions = useMemo(() => {
    if (options.length > 0) {
      return [{ value: SELECT_ALL, label: t('全选') }, ...options];
    }
    return [];
  }, [optionsMemo]);

  const optionRender = useMemo(() => (option => (
    <Space>
      {option.data.tag != null && <Tag>{option.data.tag}</Tag>}
      {option.data.label}
    </Space>
  )) as ASelectProps['optionRender'], []);

  const onSearch = (val: string): void => {
    setKeyWords(val);
  };

  const normalizedKeyWords = useMemo(() => keyWords.trim().toLowerCase(), [keyWords]);
  const optionsLowerCase = useMemo(
    () => options.map(item => ({
      value: item.value,
      normalizedLabel: (item.label ?? '').toLowerCase()
    })),
    [optionsMemo]
  );
  const allOptionValues = useMemo(() => options.map(item => item.value), [optionsMemo]);
  const filterValues = useMemo(
    () => {
      if (normalizedKeyWords.length === 0) {
        return allOptionValues;
      }

      const values: string[] = [];
      for (let index = 0; index < optionsLowerCase.length; index += 1) {
        const option = optionsLowerCase[index];
        if (option.normalizedLabel.includes(normalizedKeyWords)) {
          values.push(option.value);
        }
      }
      return values;
    },
    [allOptionValues, normalizedKeyWords]
  );
  const filterValueSet = useMemo(() => new Set(filterValues), [filterValues]);
  const selectedValueSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    const realValue = removeSelectAll(selected);
    if (!Array.isArray(value) && realValue.length > 0) {
      setSelected([]);
      return;
    }
    if (Array.isArray(value) && !isSameArrayInOrder(value, realValue)) {
      if (value.length === options.length && options.length > 0) {
        setSelected([SELECT_ALL, ...value]);
      } else {
        setSelected(value);
      }
    }
  }, [value]);

  useEffect(() => {
    if (!selectedValueSet.has(SELECT_ALL)) {
      if (filterValues.length > 0 && containsAllValues(selectedValueSet, filterValues)) {
        setSelected([SELECT_ALL, ...selected]);
      }
    } else {
      if (containsAnyMissingValue(selectedValueSet, filterValues)) {
        setSelected(removeSelectAll(selected));
      }
    }
  }, [filterValues, selectedValueSet]);

  useEffect(() => {
    const root = selectRef.current?.nativeElement;
    if (root == null) return;

    const update = (): void => setMinWidth(root.offsetWidth);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(root);
    return (): void => ro.disconnect();
  }, []);

  const onSelectHandle = useCallback((selectValue: string): void => {
    if (selectValue !== SELECT_ALL) {
      const newSelectedSet = new Set(selected);
      newSelectedSet.add(selectValue);
      const isAllSelected = containsAllValues(newSelectedSet, filterValues);
      const newSelected = Array.from(newSelectedSet);
      if (isAllSelected) {
        setSelected([SELECT_ALL, ...newSelected]);
        setKeyWords('');
      } else {
        setSelected(newSelected);
      }
      onChange(removeSelectAll(newSelected));
      return;
    }
    const newSelected = Array.from(filterValueSet);
    setSelected([SELECT_ALL, ...newSelected]);
    onChange(newSelected);
    setKeyWords('');
  }, [filterValueSet, filterValues, onChange, selected]);

  const onDeselectHandle = useCallback((selectValue: string): void => {
    if (selectValue !== SELECT_ALL) {
      const newSelectedSet = new Set(selected);
      newSelectedSet.delete(selectValue);
      newSelectedSet.delete(SELECT_ALL);
      const newSelected = Array.from(newSelectedSet);
      setSelected(newSelected);
      onChange(newSelected);
      return;
    }
    const newSelected
      = selected.filter(item => !filterValueSet.has(item) && item !== SELECT_ALL);
    setSelected(newSelected);
    onChange(newSelected);
    setKeyWords('');
  }, [filterValueSet, onChange, selected]);

  const onClearHandle = useCallback((): void => {
    setKeyWords('');
    setSelected([]);
    onChange([]);
  }, [onChange]);

  const onClearTouchStart = useCallback((event: React.TouchEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onClearHandle();
    selectRef.current?.blur();
  }, [onClearHandle]);

  const onDropdownVisibleChangeHandle = useCallback((visible: boolean): void => {
    if (!visible) {
      setKeyWords('');
    }
  }, []);

  const resolvedAllowClear = allowClear !== false && allowClear != null && md === false
    ? {
      ...(typeof allowClear === 'object' ? allowClear : {}),
      clearIcon: <CloseCircleFilled onTouchStart={onClearTouchStart} />
    }
    : allowClear;

  return (
    <ASelect
      ref={selectRef}
      className={join(styles.select, props.className)}
      disabled={disabled}
      loading={loading}
      value={selected}
      mode='multiple'
      maxTagCount={maxTagCount}
      showSearch
      allowClear={resolvedAllowClear}
      placeholder={placeholder}
      filterOption={filterOption}
      searchValue={keyWords}
      autoClearSearchValue={false}
      onSelect={onSelectHandle}
      onDeselect={onDeselectHandle}
      onClear={onClearHandle}
      labelRender={labelRender}
      onSearch={onSearch}
      popupClassName={styles.selectPopup}
      dropdownStyle={md === false ? { minWidth, maxWidth: 300 } : { minWidth, maxWidth: 600 }}
      dropdownRender={props.dropdownRender}
      options={selectOptions}
      optionRender={optionRender}
      onDropdownVisibleChange={onDropdownVisibleChangeHandle}
    />
  );
});

/**
 * 搜索过滤
 */
function filterOption(input: string, option?: SelectOption): boolean {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || option?.value === 'all';
}

function containsAllValues(source: Set<string>, values: string[]): boolean {
  for (let index = 0; index < values.length; index += 1) {
    if (!source.has(values[index])) {
      return false;
    }
  }
  return true;
}

function containsAnyMissingValue(source: Set<string>, values: string[]): boolean {
  for (let index = 0; index < values.length; index += 1) {
    if (!source.has(values[index])) {
      return true;
    }
  }
  return false;
}

function isSameArrayInOrder(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
}

function removeSelectAll(values: string[]): string[] {
  if (values[0] === SELECT_ALL) {
    return values.slice(1);
  }

  return values;
}
